import { createTheme } from '@mui/material/styles';

/**
 * Shared product tokens. These deliberately mirror the public PithPlay site so
 * the marketing and application experiences feel like one product.
 */
export const brand = {
  ink: '#0F172A',
  slate: '#334155',
  copper: '#B45309',
  copperLight: '#D97706',
  copperDark: '#92400E',
  teal: '#0F766E',
  green: '#15803D',
  ivory: '#F8F5EF',
  canvas: '#FBFAF7',
  border: '#E6DED2',
  borderStrong: '#D6CCBD',
  textMuted: '#5F6671',
  /** Lightest grey that still passes 4.5:1 on white (4.9) and on the canvas (4.7). */
  textSubtle: '#6B717B',
} as const;

/**
 * Warm neutral ramp. Replaces MUI's default cool greys so `grey.*` sits with
 * the ivory surfaces and warm borders instead of fighting them.
 */
export const neutral = {
  50: '#FBFAF7',
  100: '#F8F5EF',
  200: '#F1ECE4',
  300: '#E6DED2',
  400: '#D6CCBD',
  500: '#A39A8D',
  600: '#7C7468',
  700: '#5C564D',
  800: '#3D3933',
  900: '#1F1D1A',
  A100: '#F8F5EF',
  A200: '#F1ECE4',
  A400: '#A39A8D',
  A700: '#5C564D',
} as const;

/**
 * One status palette for every status chip in the app (project, revision,
 * task, billing). `fg` is the chip text: the dark shade, which keeps about
 * 6:1 contrast on the 10% tint (the main shades fall just under 4.5:1).
 * Copper is deliberately absent - it is reserved for actions and selection.
 */
export const statusTone = {
  neutral: { main: '#475569', fg: '#334155', bg: neutral[200] },
  active: { main: brand.teal, fg: '#115E59', bg: 'rgba(15, 118, 110, 0.10)' },
  attention: { main: '#A16207', fg: '#854D0E', bg: 'rgba(161, 98, 7, 0.10)' },
  done: { main: brand.green, fg: '#166534', bg: 'rgba(21, 128, 61, 0.10)' },
  problem: { main: '#B91C1C', fg: '#991B1B', bg: 'rgba(185, 28, 28, 0.08)' },
} as const;

export type StatusTone = keyof typeof statusTone;

/**
 * Hover and keyboard-focus treatment for a card that is clickable as a whole.
 * Static cards stay flat; only these lift.
 */
export const clickableCardSx = {
  cursor: 'pointer',
  '&:hover': {
    borderColor: brand.borderStrong,
    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
  },
  '&:focus-visible': {
    outline: `2px solid ${brand.copper}`,
    outlineOffset: 2,
  },
} as const;

const theme = createTheme({
  palette: {
    primary: {
      main: brand.copper,
      light: brand.copperLight,
      dark: brand.copperDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brand.teal,
      light: '#0D9488',
      dark: '#115E59',
      contrastText: '#FFFFFF',
    },
    success: {
      main: brand.green,
      light: '#16A34A',
      dark: '#166534',
    },
    warning: {
      main: '#A16207',
      light: '#CA8A04',
      dark: '#854D0E',
    },
    error: {
      main: '#B91C1C',
      light: '#DC2626',
      dark: '#991B1B',
    },
    info: {
      main: brand.teal,
      light: '#0D9488',
      dark: '#115E59',
    },
    background: {
      default: brand.canvas,
      paper: '#FFFFFF',
    },
    text: {
      primary: brand.ink,
      secondary: brand.textMuted,
    },
    divider: brand.border,
    grey: neutral,
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: 'clamp(2.25rem, 4vw, 3.25rem)',
      fontWeight: 800,
      lineHeight: 1.08,
      letterSpacing: '-0.035em',
    },
    h2: {
      fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
      fontWeight: 800,
      lineHeight: 1.15,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.6rem',
      fontWeight: 750,
      lineHeight: 1.25,
      letterSpacing: '-0.018em',
    },
    h4: {
      fontSize: '1.35rem',
      fontWeight: 750,
      lineHeight: 1.3,
      letterSpacing: '-0.012em',
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: brand.textMuted,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.6,
      color: brand.textMuted,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
  },
  // MUI multiplies numeric `sx` radii by this base. At 10, `borderRadius: 2`
  // rendered 20px and `3` rendered 30px across the app (the "bubbly" look).
  // At 4, those same values give 8px and 12px - the intended control/panel radii.
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.canvas,
          color: brand.ink,
        },
        '::selection': { backgroundColor: 'rgba(180, 83, 9, 0.18)' },
        // Subtle, restrained scrollbars (professional polish)
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(51, 65, 85, 0.25)',
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(51, 65, 85, 0.42)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        // Flat panel with a warm border. No hover lift here: a lift on every
        // card made static panels look clickable. Clickable cards set their
        // own hover (see `clickableCardSx`).
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          border: `1px solid ${brand.border}`,
          background: '#FFFFFF',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.875rem',
          padding: '9px 18px',
          letterSpacing: 0,
          transition: 'background-color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
          boxShadow: 'none',
        },
        contained: {
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
          '&:hover': {
            boxShadow: '0 2px 6px rgba(16, 24, 40, 0.14)',
          },
        },
        containedPrimary: {
          backgroundColor: brand.copper,
          backgroundImage: 'none',
          '&:hover': {
            backgroundColor: brand.copperDark,
            backgroundImage: 'none',
          },
        },
        containedSecondary: {
          backgroundColor: brand.teal,
          backgroundImage: 'none',
          '&:hover': {
            backgroundColor: '#115E59',
            backgroundImage: 'none',
          },
        },
        outlined: {
          borderColor: '#D6CCBD',
          '&:hover': {
            borderColor: brand.copper,
            backgroundColor: 'rgba(180, 83, 9, 0.05)',
          },
        },
        sizeLarge: {
          padding: '12px 26px',
          fontSize: '0.9375rem',
          borderRadius: 9,
        },
        sizeSmall: {
          padding: '5px 12px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
            transition: 'box-shadow 0.18s ease, border-color 0.18s ease',
            '& fieldset': {
              borderColor: '#D6CCBD',
            },
            '&:hover fieldset': {
              borderColor: '#BBAF9F',
            },
            '&.Mui-focused fieldset': {
              borderColor: brand.copper,
              borderWidth: 1,
            },
            '&.Mui-focused': {
              boxShadow: '0 0 0 3px rgba(180, 83, 9, 0.12)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // Keep Paper opaque and clean — glassmorphism was applied globally and
        // muddied dialogs/menus. Elevation utilities still work where needed.
        rounded: {
          borderRadius: 12,
        },
        outlined: {
          borderColor: brand.border,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
      },
      // The default filled chip is a quiet neutral. This used to be a nested
      // `&.MuiChip-colorDefault` rule, which out-ranked colours set through
      // `sx` - so every status chip coloured that way rendered the same copper.
      // A theme variant has the same specificity as the root, so `sx` wins.
      variants: [
        {
          props: { variant: 'filled', color: 'default' },
          style: {
            backgroundColor: neutral[200],
            color: brand.slate,
          },
        },
      ],
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 44,
          minWidth: 0,
          paddingLeft: 14,
          paddingRight: 14,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
          background: '#FFFFFF',
          border: `1px solid ${brand.border}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: brand.ivory,
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: brand.textMuted,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: brand.border,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(180, 83, 9, 0.035)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 0 rgba(15, 23, 42, 0.08)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.20)',
          border: `1px solid ${brand.border}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 28px rgba(16, 24, 40, 0.14)',
          border: `1px solid ${brand.border}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: brand.ink,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 8,
          padding: '6px 10px',
        },
      },
    },
  },
});

export default theme;